import { Link, usePage } from "@inertiajs/react"
import { LockKeyhole } from "lucide-react"

import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"

/**
 * The till's state, as every staff screen that offers an order-taking control
 * sees it.
 *
 * **The browser decides nothing here.** `Location::CashRegisterGate` is what
 * refuses the write, and it goes on refusing whatever this renders — the tablet
 * on the pass keeps POSTing after the cashier signs the Z, and hiding is not
 * blocking. What this module exists for is the other half of the same rule: a
 * control that will be refused must not look like one that will work. Letting a
 * waiter build a three-line order and only then bounce them to the dashboard is
 * the promise-then-refuse the honest-UI rule is written against.
 *
 * `null` — not `{ open: false }` — when the restaurant does not run a register.
 * "No aplica" and "la caja está cerrada" are different sentences and only the
 * second one is a banner; `Staff::Location::BaseController#shared_cash_drawer`
 * sends nil for the first so no screen has to know the feature key.
 */
export type CashDrawer = {
  open: boolean
  /** Where to go and open one. Sent by the server so no route lives twice. */
  path: string
} | null

const I18N_BASE = "ui.react.features.staff.cash_drawer"

/**
 * `usePage` throws outside an Inertia page, and both workspaces that read this
 * are mounted bare by their Vitest component tests. A component asked "is the
 * drawer shut" with no page to answer from has to say "this restaurant keeps no
 * register" — the same nil the server sends — rather than take the screen down.
 *
 * The `useContext` inside `usePage` runs before it throws, so hook order is
 * unaffected and this stays a legal hook.
 */
export function useCashDrawer(): CashDrawer {
  try {
    return (usePage().props as { cashDrawer?: CashDrawer }).cashDrawer ?? null
  } catch {
    return null
  }
}

/**
 * Whether the drawer is shut on a restaurant that keeps one. The single
 * predicate every caller reads, so "register off" can never be mistaken for
 * "register closed".
 */
export function cashDrawerBlocksOrders(drawer: CashDrawer): boolean {
  return drawer !== null && !drawer.open
}

/** The sentence that goes on a control the closed drawer will refuse. */
export function cashDrawerBlockedReason(drawer: CashDrawer): string | null {
  return cashDrawerBlocksOrders(drawer) ? t(`${I18N_BASE}.closed_orders`) : null
}

/**
 * The explanation, with the way out of it.
 *
 * The link is the point. A cashier reading "la caja está cerrada" on the floor
 * screen already knew that; what they need is one tap to the till, and the
 * route comes from the server rather than being written here a second time.
 *
 * Renders nothing when the drawer is open or the restaurant keeps none, so
 * callers can mount it unconditionally.
 */
export function CashDrawerClosedNotice({ className }: { className?: string }) {
  const drawer = useCashDrawer()

  if (!cashDrawerBlocksOrders(drawer)) return null

  return (
    <div
      className={className}
      data-testid="cash-drawer-closed-notice"
      role="status"
    >
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3">
        {/* The dot rule: colour lives in a small mark, and the words beside it
            carry the same meaning without it. */}
        <LockKeyhole aria-hidden="true" className="size-4 shrink-0 text-warning" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t(`${I18N_BASE}.closed_title`)}</p>
          <p className="text-xs text-muted-foreground">{t(`${I18N_BASE}.closed_orders`)}</p>
        </div>
        <Button asChild size="sm" variant="outline" data-testid="cash-drawer-open-link">
          <Link href={drawer!.path}>{t(`${I18N_BASE}.open_register`)}</Link>
        </Button>
      </div>
    </div>
  )
}
